import {
  buildDurationText,
  resolvePlanDuration,
  normalizePlanDuration,
  durationToMonthsForCalc,
  addDurationToDate,
} from '@/lib/planDuration';
import { countWeekdayLocalDaysInclusive } from '@/lib/investmentDailyInterestCalc';
import { localDateKey, sanitizeTimezone } from '@/lib/userTimezone';
import { isCompoundPlan } from '@/lib/planInterestType';

/** Parse "$100K", "$130", etc. */
export function parsePlanMoney(str) {
  if (str == null) return 0;
  const raw = String(str);
  if (!raw) return 0;
  const unitMatch = raw.match(/([KMB])\b/i);
  const u = unitMatch ? unitMatch[1].toUpperCase() : '';
  const mult = u === 'K' ? 1e3 : u === 'M' ? 1e6 : u === 'B' ? 1e9 : 1;
  const digits = raw.replace(/[^0-9.\-]/g, '');
  const n = Number(digits);
  if (!Number.isFinite(n)) return 0;
  return n * mult;
}

/**
 * @returns {{ compound: boolean, rate: number, period: 'day'|'month'|'year'|'term'|null }}
 */
export function parsePlanInterestRate(rateStr) {
  const s = String(rateStr || '').toLowerCase();
  if (/compound/i.test(s)) {
    return { compound: true, rate: 0, period: null };
  }
  const m = s.match(/(-?\d+(?:\.\d+)?)\s*%/);
  const rate = m ? Number(m[1]) / 100 : 0;
  if (/\/day|\bper\s*day\b|\bdaily\b/.test(s)) {
    return { compound: false, rate, period: 'day' };
  }
  if (/\/mo|\/month|\bper\s*month\b|\bmonthly\b/.test(s)) {
    return { compound: false, rate, period: 'month' };
  }
  if (/\/year|\byr\b|\bannual\b|\byearly\b/.test(s)) {
    return { compound: false, rate, period: 'year' };
  }
  return { compound: false, rate, period: null };
}

/** Infer rate period when admin omits suffix — always normalized to daily accrual in the app. */
export function resolveInterestPeriod(parsed, term) {
  if (parsed.compound) return 'day';
  if (parsed.period === 'day') return 'day';
  if (parsed.period === 'year') return 'day';
  return 'day';
}

function deriveCompoundMonthlyRate({ totalReturn, min, months }) {
  const target = parsePlanMoney(totalReturn);
  const principal = min > 0 ? min : 100000;
  if (!Number.isFinite(target) || target <= principal || months < 1 / 30) return 0;
  return Math.pow(target / principal, 1 / months) - 1;
}

export function planTermDays(term) {
  const { days, months } = normalizePlanDuration(term);
  if (days > 0 || months > 0) return Math.max(1, days + months * 30);
  return 30;
}

/** Mon–Fri days in a plan term (user local calendar), from a start date. */
export function weekdayCountInPlanTerm(term, timezone = 'UTC', startDate = new Date()) {
  const end = addDurationToDate(startDate, term);
  if (!end) return planTermDays(term);
  const tz = sanitizeTimezone(timezone);
  const fromKey = localDateKey(startDate, tz);
  const toKey = localDateKey(end, tz);
  return Math.max(1, countWeekdayLocalDaysInclusive(fromKey, toKey, tz));
}

/** Display-only marketing copy — not used for interest math. */
export function dailyRateFromPlanDailyProfit(plan) {
  const profit = parsePlanMoney(plan?.dailyProfit);
  const ref = parsePlanMoney(plan?.minPrincipal);
  if (profit > 0 && ref > 0) return profit / ref;
  return 0;
}

/** Extract percent number from strings like "Upto 2.5%", "2.5%/day". */
export function parsePlanInterestPercent(rateStr) {
  const parsed = parsePlanInterestRate(rateStr);
  return parsed.rate > 0 ? parsed.rate * 100 : 0;
}

export function resolvePlanEarningDays(plan) {
  const n = Math.floor(Number(plan?.earningDays || 0));
  return n > 0 ? n : 0;
}

/** Map parsed admin interestRate to a daily decimal rate (null when no usable %). */
function dailyRateFromParsedInterest(parsed) {
  if (!(parsed.rate > 0) || parsed.compound) return null;
  if (parsed.period === 'day' || parsed.period === null) return parsed.rate;
  if (parsed.period === 'year') return parsed.rate / 365;
  if (parsed.period === 'month') return parsed.rate / 30;
  return parsed.rate;
}

/** Daily accrual rate for investments — explicit interestRate % always wins over compound/totalReturn. */
export function accrualDailyRateFromPlan(plan) {
  const parsed = parsePlanInterestRate(plan?.interestRate);
  const term = resolvePlanDuration(plan);
  const explicitDaily = dailyRateFromParsedInterest(parsed);
  if (explicitDaily != null) return explicitDaily;

  if (isCompoundPlan(plan) || parsed.compound) {
    const monthlyRate = deriveCompoundMonthlyRate({
      totalReturn: plan?.totalReturn,
      min: parsePlanMoney(plan?.minPrincipal) || 100000,
      months: durationToMonthsForCalc(term),
    });
    const days = weekdayCountInPlanTerm(term);
    const min = parsePlanMoney(plan?.minPrincipal) || 100000;
    const total = min * Math.pow(1 + monthlyRate, durationToMonthsForCalc(term));
    const totalInterest = Math.max(0, total - min);
    return days > 0 ? totalInterest / (min * days) : 0;
  }

  if (parsed.period === 'day') return parsed.rate || 0;
  if (parsed.period === 'year') return (parsed.rate || 0) / 365;
  if (parsed.period === 'month') return (parsed.rate || 0) / 30;
  /** Bare "2.5%" with no suffix → treat as daily %. */
  return parsed.rate || 0;
}

/** @deprecated alias — use accrualDailyRateFromPlan */
export function resolveDailyRateFromPlan(plan) {
  return accrualDailyRateFromPlan(plan);
}

export function formatPlanRateSummary(plan) {
  const term = resolvePlanDuration(plan);
  const durationLabel = buildDurationText(term) || String(plan?.duration || '');
  const dailyRate = resolveDailyRateFromPlan(plan);
  const pct = (dailyRate * 100).toFixed(4).replace(/\.?0+$/, '');

  if (isCompoundPlan(plan)) {
    return {
      durationLabel,
      summary: `~${pct}%/day (from compound plan)`,
      periodLabel: 'day',
    };
  }

  const parsed = parsePlanInterestRate(plan?.interestRate);
  if (parsed.period === 'day') {
    return { durationLabel, summary: `${pct}%/day`, periodLabel: 'day' };
  }
  if (parsed.period === 'year') {
    return { durationLabel, summary: `${pct}%/day (from ${(parsed.rate * 100).toFixed(2)}%/year)`, periodLabel: 'day' };
  }
  if (parsed.period === 'month' || /\/mo|month|monthly/i.test(String(plan?.interestRate || ''))) {
    return {
      durationLabel,
      summary: `${pct}%/day (${(parsed.rate * 100).toFixed(2)}%/mo)`,
      periodLabel: 'day',
    };
  }
  return {
    durationLabel,
    summary: `${pct}%/day (${(parsed.rate * 100).toFixed(2)}% monthly equiv.)`,
    periodLabel: 'day',
  };
}

/**
 * Projected returns — interest accrues on principal each weekday (Mon–Fri, local time)
 * after each calendar day ends (midnight). Same-day purchase time does not matter.
 */
export function calculatePlanReturns(principal, plan, timezone = 'UTC') {
  const amount = Number(principal) || 0;
  const empty = {
    periodInterest: 0,
    totalInterest: 0,
    totalWithdrawal: 0,
    periodLabel: 'day',
    dailyRate: 0,
    termDays: 0,
  };
  if (!plan || amount <= 0) return empty;

  const term = resolvePlanDuration(plan);
  const earningDays = resolvePlanEarningDays(plan);
  const termDays =
    earningDays > 0 ? earningDays : weekdayCountInPlanTerm(term, timezone);
  const dailyRate = resolveDailyRateFromPlan(plan);
  const periodInterest = round2(amount * dailyRate);
  const totalInterest = round2(amount * dailyRate * termDays);

  return {
    periodInterest,
    totalInterest,
    totalWithdrawal: round2(amount + totalInterest),
    periodLabel: 'day',
    dailyRate,
    termDays,
  };
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** English labels for landing calculator (home page is not translated). */
export function periodInterestFieldLabel(periodLabel) {
  switch (periodLabel) {
    case 'term':
      return 'Plan Interest';
    case 'day':
      return 'Daily Interest';
    case 'year':
      return 'Monthly Equivalent';
    default:
      return 'Monthly Interest';
  }
}

/** i18n key for projected-return period label (user/agent panel). */
export function investmentPeriodInterestKey(periodLabel) {
  switch (periodLabel) {
    case 'term':
      return 'user.investments.modal.periodInterestTerm';
    case 'day':
      return 'user.investments.modal.periodInterestDay';
    case 'year':
      return 'user.investments.modal.periodInterestYear';
    default:
      return 'user.investments.modal.monthlyInterest';
  }
}
